import { Component, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import Supercluster from "supercluster";
import { motion } from "framer-motion";
import { Loader2, Layers, RefreshCw, TriangleAlert, Search, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import ChapterHeading from "@/components/ChapterHeading";
import { API } from "@/lib/api";

const LAYER_COLORS = {
  "Basura": "#C03C54",
  "Alumbrado Público": "#D9A441",
  "Limpieza o Mantenimiento": "#1E7E50",
  "Calles o Vialidad": "#5A524A",
  "Áreas Verdes": "#4E7A3A",
  "Drenaje o Alcantarillado": "#35566B",
  "Situación de Riesgo": "#8C2236",
  "Construcción de Obras": "#7A3B2E",
  "Otros": "#BEB195",
};
const TAMPICO = { lat: 22.2331, lng: -97.8611 };

function layerKey(row) {
  if (row.tema && row.tema !== "Reportes Ciudadanos") return row.tema;
  return LAYER_COLORS[row.asunto] ? row.asunto : "Otros";
}

const slug = (k) => k.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function ClusteredMarkers({ rows, onSelect }) {
  const map = useMap();
  const [clusters, setClusters] = useState([]);

  const index = useMemo(() => {
    const idx = new Supercluster({ radius: 70, maxZoom: 16, minPoints: 3 });
    idx.load(
      rows.map((r, i) => ({
        type: "Feature",
        properties: { i },
        geometry: { type: "Point", coordinates: [r.lng, r.lat] },
      }))
    );
    return idx;
  }, [rows]);

  useEffect(() => {
    if (!map) return;
    const update = () => {
      const b = map.getBounds();
      if (!b) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const z = Math.max(1, Math.floor(map.getZoom() || 12));
      setClusters(index.getClusters([sw.lng(), sw.lat(), ne.lng(), ne.lat()], z));
    };
    update();
    const listener = map.addListener("idle", update);
    return () => listener.remove();
  }, [map, index]);

  const zoomToCluster = (c) => {
    const zoom = Math.min(index.getClusterExpansionZoom(c.properties.cluster_id), 18);
    map.panTo({ lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0] });
    map.setZoom(zoom);
  };

  return clusters.map((c) => {
    const [lng, lat] = c.geometry.coordinates;
    if (c.properties.cluster) {
      const count = c.properties.point_count;
      const size = 36 + Math.min(30, Math.round(Math.log10(count) * 9));
      return (
        <AdvancedMarker
          key={`c-${c.properties.cluster_id}`}
          position={{ lat, lng }}
          onClick={() => zoomToCluster(c)}
          zIndex={2}
        >
          <div
            data-testid="mapa-cluster"
            title={`${count.toLocaleString("es-MX")} reportes — clic para acercar`}
            className="flex items-center justify-center rounded-full bg-guinda text-white font-mono font-semibold border-[3px] border-white shadow-lg cursor-pointer hover:bg-guinda-dark transition-colors duration-150"
            style={{ width: size, height: size, fontSize: size > 48 ? 14 : 12 }}
          >
            {count >= 1000 ? `${(count / 1000).toFixed(1).replace(".0", "")}k` : count}
          </div>
        </AdvancedMarker>
      );
    }
    const row = rows[c.properties.i];
    if (!row) return null;
    return (
      <AdvancedMarker
        key={`p-${row.folio}-${c.properties.i}`}
        position={{ lat, lng }}
        onClick={() => onSelect(row)}
      >
        <div
          className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-md cursor-pointer hover:scale-150 transition-transform duration-150"
          style={{ background: LAYER_COLORS[layerKey(row)] || "#BEB195" }}
          aria-hidden="true"
        />
      </AdvancedMarker>
    );
  });
}

function LayerPanel({ layers, active, onToggle, onAll, onNone, rows, status }) {
  const map = useMap();
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);

  const normalizeColonia = (c) =>
    c
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toUpperCase()
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^(COLONIA|COL\.?)\s+/, "");

  const toTitleCase = (s) =>
    s.toLowerCase().replace(/(^|\s)(\S)/g, (_, sp, l) => sp + l.toUpperCase());

  const colonias = useMemo(() => {
    const grouped = {};
    rows.forEach((r) => {
      if (!r.colonia) return;
      const key = normalizeColonia(r.colonia);
      if (!key) return;
      if (!grouped[key]) grouped[key] = { key, label: toTitleCase(key), count: 0 };
      grouped[key].count += 1;
    });
    return Object.values(grouped).sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [rows]);

  const matches =
    q.trim().length >= 2
      ? colonias.filter((c) => c.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6)
      : [];

  const flyTo = (entry) => {
    const pts = rows.filter((r) => r.colonia && normalizeColonia(r.colonia) === entry.key);
    if (!pts.length || !map) return;
    const lat = pts.reduce((a, r) => a + r.lat, 0) / pts.length;
    const lng = pts.reduce((a, r) => a + r.lng, 0) / pts.length;
    onAll();
    map.panTo({ lat, lng });
    map.setZoom(15);
    setQ(entry.label);
    setFocus(false);
    toast.success(`${entry.count.toLocaleString("es-MX")} reportes en Col. ${entry.label}`);
  };

  return (
    <aside
      data-testid="mapa-panel"
      className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 flex flex-col gap-5 lg:max-h-[600px]"
    >
      <div>
        <label
          htmlFor="mapa-buscar-colonia"
          className="font-mono text-[11px] tracking-[0.25em] uppercase text-arena inline-flex items-center gap-2"
        >
          <MapPin className="w-4 h-4" aria-hidden="true" /> Buscar colonia
        </label>
        <div className="relative mt-2">
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 focus-within:ring-2 ring-guinda transition-shadow duration-200">
            <Search className="w-4 h-4 text-guinda shrink-0" aria-hidden="true" />
            <input
              id="mapa-buscar-colonia"
              data-testid="mapa-buscar-colonia"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocus(true)}
              onBlur={() => setTimeout(() => setFocus(false), 180)}
              placeholder="Ej. Centro, Primavera…"
              className="w-full bg-transparent text-sm text-gris-charcoal placeholder:text-gris/50 focus:outline-none"
            />
          </div>
          {focus && matches.length > 0 && (
            <div
              data-testid="mapa-colonia-resultados"
              className="absolute z-20 mt-2 w-full bg-white rounded-2xl border border-border shadow-xl overflow-hidden"
            >
              {matches.map((c, i) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`mapa-colonia-${i}`}
                  onMouseDown={() => flyTo(c)}
                  className="w-full text-left px-4 py-2.5 text-sm text-gris-charcoal hover:bg-arena-light flex justify-between gap-2 border-b border-border/50 last:border-0 transition-colors duration-150"
                >
                  <span>Col. {c.label}</span>
                  <span className="font-mono text-xs text-gris/70">{c.count.toLocaleString("es-MX")}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-arena inline-flex items-center gap-2">
          <Layers className="w-4 h-4" aria-hidden="true" /> Capas
        </span>
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            data-testid="mapa-activar-todas"
            onClick={onAll}
            className="text-guinda-light hover:text-white transition-colors duration-150"
          >
            Todas
          </button>
          <span className="text-white/30">·</span>
          <button
            type="button"
            data-testid="mapa-apagar-todas"
            onClick={onNone}
            className="text-arena/70 hover:text-white transition-colors duration-150"
          >
            Ninguna
          </button>
        </div>
      </div>

      <div data-testid="mapa-layer-checklist" className="flex flex-col gap-1 overflow-y-auto pr-1">
        {status === "loading" && (
          <span className="inline-flex items-center gap-2 text-sm text-arena/80 px-3 py-2">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando capas…
          </span>
        )}
        {layers.map(([k, count]) => {
          const color = LAYER_COLORS[k] || "#BEB195";
          const on = active.has(k);
          return (
            <button
              key={k}
              type="button"
              role="checkbox"
              aria-checked={on}
              data-testid={`mapa-layer-toggle-${slug(k)}`}
              onClick={() => onToggle(k)}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/10 transition-colors duration-150 text-left"
            >
              <span
                className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors duration-150"
                style={{ borderColor: color, background: on ? color : "transparent" }}
              >
                {on && <Check className="w-3.5 h-3.5 text-white" aria-hidden="true" />}
              </span>
              <span className={`text-sm flex-1 transition-colors duration-150 ${on ? "text-white" : "text-white/55"}`}>
                {k}
              </span>
              <span className="font-mono text-xs text-arena/80">{count.toLocaleString("es-MX")}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-auto text-xs text-arena/70 leading-relaxed">
        Tip: haz clic en un círculo para acercar a la zona y en un punto para ver el detalle del reporte.
      </p>
    </aside>
  );
}

function MapFallback({ reason }) {
  return (
    <div data-testid="mapa-fallback" className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-gris-charcoal">
      <span className="p-4 rounded-2xl bg-guinda/20 text-guinda-light mb-5">
        <TriangleAlert className="w-8 h-8" aria-hidden="true" />
      </span>
      <p className="font-serif text-2xl text-white">El mapa necesita un ajuste en la clave de Google</p>
      <p className="mt-3 text-arena max-w-lg text-sm leading-relaxed">
        {reason === "billing"
          ? "La clave de Google Maps no tiene la facturación o la API activada."
          : "La clave de Google Maps aún no tiene activada la “Maps JavaScript API”."}{" "}
        Actívala en{" "}
        <a
          href="https://console.cloud.google.com/apis/library/maps-backend.googleapis.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-guinda-light"
        >
          Google Cloud Console → Biblioteca → Maps JavaScript API
        </a>{" "}
        y el mapa cargará automáticamente.
      </p>
      <p className="mt-6 font-mono text-xs tracking-widest uppercase text-arena/60">
        Los datos del padrón siguen disponibles en las capas superiores
      </p>
    </div>
  );
}

class MapBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err) {
    console.warn("Map render error:", err?.message);
  }
  render() {
    if (this.state.failed) return <MapFallback />;
    return this.props.children;
  }
}

export default function Mapa() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [active, setActive] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    const onErr = () => setMapError(true);
    window.addEventListener("gmaps-error", onErr);
    return () => window.removeEventListener("gmaps-error", onErr);
  }, []);

  const load = () => {
    setStatus("loading");
    axios
      .get(`${API}/mapa/datos`, { timeout: 45000 })
      .then((r) => {
        setRows(r.data.rows || []);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  };

  useEffect(load, []);

  const layers = useMemo(() => {
    const counts = {};
    rows.forEach((r) => {
      const k = layerKey(r);
      counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const visible = useMemo(() => {
    return rows.filter((r) => active.has(layerKey(r)));
  }, [rows, active]);

  const toggle = (k) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const activateAll = () => setActive(new Set(layers.map(([k]) => k)));
  const deactivateAll = () => setActive(new Set());

  return (
    <section id="mapa" data-testid="mapa-section" className="bg-gris-charcoal py-20 md:py-28 text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterHeading
          light
          num="04 · Mapa Interactivo"
          title="Impacto en tu Colonia"
          desc="Conoce las gestiones realizadas en tu colonia, Tampico te Cuida. Activa o desactiva las capas por tema."
        />

        {!apiKey || mapError ? (
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mapa-container"
            className="map-warm relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl h-[480px] bg-[#221e1b]"
          >
            <MapFallback />
          </motion.div>
        ) : (
          <APIProvider apiKey={apiKey}>
            <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-stretch">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid="mapa-container"
                className="map-warm relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl h-[480px] md:h-[600px] bg-[#221e1b]"
              >
                <MapBoundary>
                  <Map
                    defaultCenter={TAMPICO}
                    defaultZoom={12}
                    mapId="DEMO_MAP_ID"
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    zoomControl
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl
                    style={{ width: "100%", height: "100%" }}
                  >
                    <ClusteredMarkers rows={visible} onSelect={setSelected} />
                    {selected && (
                      <InfoWindow
                        position={{ lat: selected.lat, lng: selected.lng }}
                        onCloseClick={() => setSelected(null)}
                      >
                        <div className="max-w-[260px] text-gris-charcoal p-1" data-testid="mapa-infowindow">
                          <p className="font-mono text-[10px] tracking-widest uppercase text-guinda">
                            {selected.folio} · {layerKey(selected)}
                          </p>
                          <p className="font-semibold text-sm mt-1">{selected.asunto}</p>
                          {selected.detalle && <p className="text-xs mt-1 text-gris">{selected.detalle}</p>}
                          {selected.colonia && (
                            <p className="text-xs mt-2 text-gris">Col. {selected.colonia}</p>
                          )}
                          <span
                            className={`inline-block mt-2 text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${
                              selected.estatus === "Atendido"
                                ? "bg-verde-soft text-verde"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {selected.estatus}
                          </span>
                        </div>
                      </InfoWindow>
                    )}
                  </Map>
                </MapBoundary>

                {status === "ok" && active.size === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    data-testid="mapa-empty-state"
                    className="absolute inset-0 z-10 bg-gris-charcoal/60 backdrop-blur-[3px] flex items-center justify-center px-6"
                  >
                    <div className="text-center max-w-md bg-gris-charcoal/85 border border-white/10 rounded-3xl px-8 py-10 shadow-2xl">
                      <span className="inline-flex p-3.5 rounded-2xl bg-guinda/25 text-guinda-light mb-5">
                        <Layers className="w-7 h-7" aria-hidden="true" />
                      </span>
                      <p className="font-serif text-2xl text-white">Explora los reportes por tema</p>
                      <p className="mt-3 text-arena/90 text-sm leading-relaxed">
                        Las capas están apagadas. Marca las categorías que quieras visualizar en el panel
                        de la derecha o busca tu colonia para ir directo a tu zona.
                      </p>
                      <button
                        type="button"
                        data-testid="mapa-empty-activar-todas"
                        onClick={activateAll}
                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-guinda hover:bg-guinda-dark text-white px-7 py-3 text-sm font-medium transition-colors duration-200"
                      >
                        <Layers className="w-4 h-4" aria-hidden="true" />
                        Activar todas las capas
                      </button>
                    </div>
                  </motion.div>
                )}

                {status === "loading" && (
                  <div className="absolute inset-0 bg-gris-charcoal/70 backdrop-blur-sm flex items-center justify-center">
                    <span className="inline-flex items-center gap-3 text-arena">
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      Cargando datos del padrón público…
                    </span>
                  </div>
                )}
                {status === "error" && (
                  <div className="absolute inset-0 bg-gris-charcoal/85 flex flex-col items-center justify-center text-center px-8">
                    <p className="font-serif text-2xl text-white">No se pudieron cargar los datos</p>
                    <p className="mt-2 text-arena text-sm max-w-md">
                      Verifica que la hoja de Google Sheets sea de lectura pública.
                    </p>
                    <button
                      type="button"
                      data-testid="mapa-retry-button"
                      onClick={load}
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-guinda hover:bg-guinda-dark text-white px-6 py-2.5 text-sm transition-colors duration-200"
                    >
                      <RefreshCw className="w-4 h-4" aria-hidden="true" /> Reintentar
                    </button>
                  </div>
                )}
              </motion.div>

              <LayerPanel
                layers={layers}
                active={active}
                onToggle={toggle}
                onAll={activateAll}
                onNone={deactivateAll}
                rows={rows}
                status={status}
              />
            </div>
          </APIProvider>
        )}

        <p data-testid="mapa-stats" className="mt-5 text-sm text-arena/80 font-mono tracking-wide">
          {status === "ok"
            ? active.size === 0
              ? "Capas apagadas · marca las que quieras visualizar en el panel"
              : `${visible.length.toLocaleString("es-MX")} registros georreferenciados · Los círculos agrupan reportes por zona — haz clic para acercar`
            : "Fuente: padrón municipal en Google Sheets (lectura pública)"}
        </p>
      </div>
    </section>
  );
}
